import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Layers, CheckCircle2 } from "lucide-react";

export function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    try { const u = await login(form.email, form.password); toast.success("Welcome back"); nav(u.role === "ADMIN" || u.role === "SUPER_ADMIN" ? "/admin" : "/"); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };
  return <AuthShell title="Welcome back" testId="login-page">
    <form onSubmit={submit} className="flex flex-col gap-3">
      <input data-testid="login-email" type="email" required placeholder="Email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="tl-input" />
      <input data-testid="login-password" type="password" required placeholder="Password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} className="tl-input" />
      {err && <div data-testid="login-error" className="text-sm text-red-600">{err}</div>}
      <button data-testid="login-submit" className="tl-btn-primary justify-center">Login</button>
      <div className="flex justify-between text-sm text-[#475569] mt-2">
        <Link data-testid="login-forgot" to="/forgot-password" className="text-[#0891B2] hover:underline">Forgot password?</Link>
        <Link to="/register" className="text-[#0891B2] hover:underline">Create account</Link>
      </div>
    </form>
  </AuthShell>;
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (e) {
      // The endpoint always returns 200; if we ever see an error it's networky.
      toast.error(formatApiError(e.response?.data?.detail) || "Please try again in a moment.");
    } finally { setBusy(false); }
  };
  if (sent) {
    return <AuthShell title="Check your inbox" testId="forgot-sent">
      <div className="flex flex-col items-center text-center gap-3">
        <div className="w-12 h-12 rounded-full bg-[#0891B2]/15 border border-[#0891B2]/30 flex items-center justify-center"><CheckCircle2 className="w-6 h-6 text-[#0891B2]" /></div>
        <p className="text-[#475569]">If <span className="text-slate-900">{email}</span> is registered, we've sent a password reset link. It expires in 30 minutes.</p>
        <p className="text-xs text-[#64748B]">Didn't get it? Check your spam folder, then try again in a few minutes.</p>
        <Link to="/login" className="tl-btn-secondary mt-2" data-testid="forgot-back-login">Back to login</Link>
      </div>
    </AuthShell>;
  }
  return <AuthShell title="Reset your password" testId="forgot-page">
    <form onSubmit={submit} className="flex flex-col gap-3">
      <p className="text-sm text-[#475569] mb-2">Enter your email and we'll send you a link to choose a new password.</p>
      <input data-testid="forgot-email" type="email" required placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} className="tl-input" />
      <button data-testid="forgot-submit" disabled={busy} className="tl-btn-primary justify-center disabled:opacity-50">{busy ? "Sending…" : "Send reset link"}</button>
      <div className="text-sm text-center mt-2"><Link to="/login" className="text-[#0891B2] hover:underline">Back to login</Link></div>
    </form>
  </AuthShell>;
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (!token) {
    return <AuthShell title="Invalid reset link" testId="reset-invalid">
      <p className="text-sm text-[#475569]">This link is missing a reset token. Please request a new one from the forgot-password page.</p>
      <Link to="/forgot-password" className="tl-btn-primary justify-center mt-4" data-testid="reset-request-new">Request a new link</Link>
    </AuthShell>;
  }

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (form.password !== form.confirm) { setErr("Passwords don't match."); return; }
    if (form.password.length < 8) { setErr("Password must be at least 8 characters."); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: form.password });
      toast.success("Password updated. Please sign in.");
      nav("/login");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Reset failed.");
    } finally { setBusy(false); }
  };

  return <AuthShell title="Choose a new password" testId="reset-page">
    <form onSubmit={submit} className="flex flex-col gap-3">
      <p className="text-sm text-[#475569] mb-1">Pick a strong password — this will sign you out on all your devices.</p>
      <input data-testid="reset-password" type="password" required minLength={8} placeholder="New password (min 8)" value={form.password} onChange={e => setForm({...form, password: e.target.value})} className="tl-input" />
      <input data-testid="reset-confirm" type="password" required minLength={8} placeholder="Confirm new password" value={form.confirm} onChange={e => setForm({...form, confirm: e.target.value})} className="tl-input" />
      {err && <div data-testid="reset-error" className="text-sm text-red-600">{err}</div>}
      <button data-testid="reset-submit" disabled={busy} className="tl-btn-primary justify-center disabled:opacity-50">{busy ? "Updating…" : "Update password"}</button>
    </form>
  </AuthShell>;
}

export function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "CUSTOMER" });
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    try { const u = await register(form); toast.success("Account created!");
      nav(form.role === "EXPERT" ? "/join/expert" : form.role === "PRINT_PARTNER" ? "/join/partner" : "/");
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };
  return <AuthShell title="Create your account" testId="register-page">
    <form onSubmit={submit} className="flex flex-col gap-3">
      <input data-testid="reg-name" required placeholder="Full name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="tl-input" />
      <input data-testid="reg-email" type="email" required placeholder="Email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="tl-input" />
      <input data-testid="reg-password" type="password" required minLength={8} placeholder="Password (min 8)" value={form.password} onChange={e => setForm({...form, password: e.target.value})} className="tl-input" />
      <select data-testid="reg-role" value={form.role} onChange={e => setForm({...form, role: e.target.value})} className="tl-input">
        <option value="CUSTOMER">I am a Customer</option>
        <option value="EXPERT">I am an Expert / Designer</option>
        <option value="PRINT_PARTNER">I am a 3D Printer Owner</option>
      </select>
      {err && <div data-testid="reg-error" className="text-sm text-red-600">{err}</div>}
      <button data-testid="reg-submit" className="tl-btn-primary justify-center">Create Account</button>
      <div className="text-sm text-[#475569] text-center mt-2">Already have an account? <Link to="/login" className="text-[#0891B2]">Login</Link></div>
    </form>
  </AuthShell>;
}

function AuthShell({ children, title, testId }) {
  return (
    <div data-testid={testId} className="min-h-[80vh] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#2563EB] to-[#0891B2] flex items-center justify-center"><Layers className="w-5 h-5 text-slate-900" /></div>
          <span className="font-display font-bold text-xl">Trinity Layers</span>
        </Link>
        <div className="tl-card p-8">
          <h1 className="text-2xl font-display font-bold mb-6">{title}</h1>
          {children}
        </div>
      </div>
    </div>
  );
}
